import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../App/hooks";
import { ITableUI } from "../../TableTemplate/tableTemplate";
import {
  IStepAddOrderFormValues,
  IStepOrderPausePlayStatus,
  IStepOrderTableReport,
  StepOptionOrderType,
} from "../../../Interfaces/stepInterfaces";
import {
  setStepAddOrderFormValues,
  setStepOrderTableFetchDataFlag,
} from "../../../Features/Step/stepSlice";
import { CommonTableUI } from "../../TableTemplate/common";
import { StepOrderTableAdditional } from "./StepOrderTableAdditional";
import { getStepOrderPausePlayStatus } from "../../../Requests/stepRequests";
import { store } from "../../../App/store";
import { StepOrderPausePlayAcceptedContext } from "./StepOrderPausePlayContext";

type StatusSnapshot = {
  status: IStepOrderPausePlayStatus | null;
  requestedAt: number;
  receivedAt: number;
};
type AcceptedCommand = { at: number; isDelete: boolean };

const StepOrderTableUI: React.FC<ITableUI<IStepOrderTableReport>> = ({
  reports,
  onAction,
  onTableHeaderClick,
  onTableRowClick,
  sortingType,
  setSortingType,
}: ITableUI<IStepOrderTableReport>) => {
  const dispatch = useAppDispatch();
  const [statuses, setStatuses] = useState<Record<number, StatusSnapshot>>({});
  const [acceptedCommands, setAcceptedCommands] = useState<Record<number, AcceptedCommand>>({});
  const loggedTerminalCommands = useRef(new Set<string>());
  const refreshedDeletedIds = useRef(new Set<number>());
  const onPausePlayAccepted = useCallback((orderIds: number[], acceptedAt: number, isDelete = false) => {
    setAcceptedCommands((previous) => {
      const next = { ...previous };
      for (const id of orderIds) next[id] = { at: acceptedAt, isDelete };
      return next;
    });
  }, []);
  const reportRequestedAt =
    (reports as typeof reports & { pausePlayStatusRequestedAt?: number })
      .pausePlayStatusRequestedAt ?? 0;
  const latestReportRequestedAt = useRef(reportRequestedAt);
  latestReportRequestedAt.current = reportRequestedAt;

  // A slower full report must not roll back a newer lightweight status response.
  const rows = reports.report.map((row) => {
    const snapshot = statuses[row.id];
    const merged = !snapshot || snapshot.requestedAt <= reportRequestedAt
      ? { ...row, pausePlayStatusRequestedAt: reportRequestedAt }
      : snapshot.status
      ? { ...row, ...snapshot.status, pausePlayStatusRequestedAt: snapshot.requestedAt }
      : { ...row, pausePlayStatusRequestedAt: snapshot.requestedAt };
    const accepted = acceptedCommands[row.id];
    return accepted?.isDelete && merged.pausePlayStatusRequestedAt < accepted.at
      ? { ...merged, deletePending: true }
      : merged;
  });
  const visibleIds = rows.map((row) => row.id).join(",");
  const deletedIds = rows.filter((row) => row.isDeleted).map((row) => row.id).join(",");

  useEffect(() => {
    if (!deletedIds) return;
    const ids = deletedIds.split(",").map(Number);
    if (!ids.some((id) => !refreshedDeletedIds.current.has(id))) return;
    ids.forEach((id) => refreshedDeletedIds.current.add(id));
    onAction?.();
  }, [deletedIds, onAction]);

  useEffect(() => {
    const completedIds: number[] = [];
    for (const [idText, command] of Object.entries(acceptedCommands)) {
      const id = Number(idText);
      const snapshot = statuses[id];
      if (!snapshot || snapshot.requestedAt < command.at) continue;

      if (snapshot.status === null) {
        completedIds.push(id);
        continue;
      }
      if (command.isDelete
        ? snapshot.status.deletePending
        : snapshot.status.pausePlayPending) continue;

      const timingKey = `${id}:${command.at}`;
      if (!command.isDelete && !loggedTerminalCommands.current.has(timingKey)) {
        loggedTerminalCommands.current.add(timingKey);
        const rowAppliedAt = performance.now();
        const action = snapshot.status.isActive ? "Play" : "Pause";
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderId: id,
          Action: action,
          Stage: "AcceptedToTerminalStatus",
          ElapsedMs: snapshot.receivedAt - command.at,
        });
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderId: id,
          Action: action,
          Stage: "TerminalStatusToRowApplied",
          ElapsedMs: rowAppliedAt - snapshot.receivedAt,
        });
      }
      completedIds.push(id);
    }

    if (!completedIds.length) return;
    setAcceptedCommands((previous) => {
      const next = { ...previous };
      completedIds.forEach((id) => delete next[id]);
      return next;
    });
  }, [acceptedCommands, statuses]);

  useEffect(() => {
    const visible = new Set(
      visibleIds ? visibleIds.split(",").map(Number) : [],
    );

    setStatuses((previous) => {
      const next: Record<number, StatusSnapshot> = {};
      let changed = false;
      for (const [idText, snapshot] of Object.entries(previous)) {
        const id = Number(idText);
        if (visible.has(id) && snapshot.requestedAt > reportRequestedAt) {
          next[id] = snapshot;
        } else {
          changed = true;
        }
      }
      return changed ? next : previous;
    });

    setAcceptedCommands((previous) => {
      const next: Record<number, AcceptedCommand> = {};
      let changed = false;
      for (const [idText, command] of Object.entries(previous)) {
        const id = Number(idText);
        if (visible.has(id) && command.at > reportRequestedAt) {
          next[id] = command;
        } else {
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [visibleIds, reportRequestedAt]);

  // A new command also restarts an existing poll, discarding reads begun before acceptance.
  const acceptedCommandKey = rows
    .map((row) => `${row.id}:${acceptedCommands[row.id]?.at ?? ""}`)
    .join(",");
  const pendingIds = rows
    .filter((row) => {
      const snapshot = statuses[row.id];
      const acceptedAt = acceptedCommands[row.id]?.at;
      if (acceptedAt !== undefined && Math.max(
        row.pausePlayStatusRequestedAt ?? 0,
        snapshot?.requestedAt ?? 0,
      ) < acceptedAt) return true;
      // pausePlayDisabled also reflects an unresolved Edit warning, which is not
      // a running command; a running Pause/Play already sets pausePlayPending.
      const pausePlayPending = row.pausePlayPending || row.deletePending;
      return pausePlayPending && !(
        snapshot?.status === null && snapshot.requestedAt > reportRequestedAt
      );
    })
    .map((row) => row.id)
    .join(",");

  useEffect(() => {
    if (!pendingIds) return;
    const ids = pendingIds.split(",").map(Number);
    const visible = new Set(visibleIds.split(",").map(Number));
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      const requestedAt = performance.now();
      let nextDelay = 750;
      try {
        const response = await getStepOrderPausePlayStatus(ids, controller.signal);
        if (controller.signal.aborted) return;
        const receivedAt = performance.now();
        if (requestedAt > latestReportRequestedAt.current) {
          setStatuses((previous) => {
            const next: Record<number, StatusSnapshot> = {};
            visible.forEach((id) => {
              if (
                previous[id] &&
                previous[id].requestedAt > latestReportRequestedAt.current
              ) {
                next[id] = previous[id];
              }
            });
            // Missing/inaccessible IDs must not inherit stale pending state forever.
            for (const id of ids) {
              if (!next[id] || next[id].requestedAt <= requestedAt) {
                next[id] = { status: null, requestedAt, receivedAt };
              }
            }
            for (const status of response) {
              if (
                visible.has(status.id) &&
                (!next[status.id] || next[status.id].requestedAt <= requestedAt)
              ) {
                next[status.id] = { status, requestedAt, receivedAt };
              }
            }
            return next;
          });
        }
      } catch {
        // Keep the existing UI on a transient status-fetch failure; retry quietly.
        nextDelay = 5000;
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(poll, nextDelay);
      }
    };

    // Command feedback must not wait for the full table query to report a pending task.
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [pendingIds, visibleIds, acceptedCommandKey]);

  const orderTableFetchDataFlag = useAppSelector(
    (store) => store.step.orderTableFetchDataFlag
  );

  useEffect(() => {
    if (!!orderTableFetchDataFlag && onAction) {
      dispatch(setStepOrderTableFetchDataFlag(false));
      onAction();
    }
  }, [dispatch, onAction, orderTableFetchDataFlag]);

  const rowClickHandler = (row: IStepOrderTableReport) => {
    if (onTableRowClick) {
      onTableRowClick(row);
      return;
    }
    dispatch(
      setStepAddOrderFormValues({
        ...store.getState().step.addOrderFormValues,
        instrumentId: row.instrument,
        instrumentType: row.instrumentType,
        optionOrderType: !row.optionType
          ? null
          : row.optionType === "Open"
            ? StepOptionOrderType.Open
            : StepOptionOrderType.Offset,
      } as IStepAddOrderFormValues),
    );
  };

  const displayReports = {
    ...reports,
    translatedHeaders: reports.translatedHeaders.filter(
      (header) => header.headerName !== "MaxConcurrentOrder",
    ),
    report: rows.filter((row) => !row.isDeleted).map((row) => ({
      ...row,
      scheduledStartTimeStr:
        row.scheduledStartTimeStr && row.scheduledStartTimeStr !== "-"
          ? row.scheduledStartTimeStr
          : "آنی",
    })),
  };

  return (
    <StepOrderPausePlayAcceptedContext.Provider value={onPausePlayAccepted}>
      <CommonTableUI
        reports={displayReports}
        onAction={onAction}
        AdditionalBody={StepOrderTableAdditional}
        withSelect
        onTableHeaderClick={onTableHeaderClick}
        onTableRowClick={rowClickHandler}
        sortingType={sortingType}
        tableBoxClassName="max-h-[500px] overflow-auto orderStrategiesTable"
        setSortingType={setSortingType}
      />
    </StepOrderPausePlayAcceptedContext.Provider>
  );
};

export default StepOrderTableUI;
